import { useContext, useEffect, useMemo, useState } from 'react';

import { Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Tooltip,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import * as FiscalPerfilAPI from '../../../api/fiscalPerfil';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeCancelar, podeEditar, podeIncluir } from '../../../hooks/usePermisionMRT';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function simNao(value) {
    return value ? 'Sim' : 'Não';
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState([]);
    const [perfilExcluir, setPerfilExcluir] = useState(null);

    const mrt = useMRTState({
        storageKey: 'fiscal-perfil.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
    });

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await FiscalPerfilAPI.listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            setDados(response.data.data || []);
            setRowCount(Number(response.data.meta?.totalRowCount) || 0);
            setIsError(false);
        } catch (error) {
            setIsError(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar os perfis fiscais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) return;

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    function novoPerfil() {
        const params = new URLSearchParams(urlParams);

        params.delete('uuid');
        params.set('btnText', 'Cadastrar');

        navigate('/FiscalPerfis/CreateOrEdit', params);
    }

    function editarPerfil(perfil) {
        const params = new URLSearchParams(urlParams);

        params.set('uuid', perfil.uuid);
        params.set('btnText', 'Atualizar');

        navigate('/FiscalPerfis/CreateOrEdit', params);
    }

    async function confirmarExclusao() {
        if (!perfilExcluir?.uuid) return;

        try {
            const response = await FiscalPerfilAPI.excluir(perfilExcluir.uuid);

            setPerfilExcluir(null);

            setFlashMessage(response.data.message, response.data.type);

            await fetchData();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível excluir o perfil fiscal.',
                error.response?.data?.type || 'error'
            );
        }
    }

    const columns = useMemo(
        () => [
            {
                id: 'perfisFiscais',
                header: 'PERFIS FISCAIS',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'codigo',
                        header: 'Código',
                    },
                    {
                        accessorKey: 'descricao',
                        header: 'Descrição',
                    },
                    {
                        accessorKey: 'versao',
                        header: 'Versão',
                        size: 80,
                    },
                    {
                        accessorKey: 'origemCadastro',
                        header: 'Origem',
                    },
                    {
                        accessorKey: 'revisaoPendente',
                        header: 'Revisão pendente',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'integracaoAtivo',
                        header: 'Publicado na API',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'integracaoTodasEmpresas',
                        header: 'Todas as empresas',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderTopToolbarCustomActions={() => (
                        <Button color="secondary" variant="contained" disabled={!podeIncluir()} onClick={novoPerfil}>
                            Novo
                        </Button>
                    )}
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editar perfil fiscal">
                                <span>
                                    <IconButton
                                        color="primary"
                                        disabled={!podeEditar()}
                                        onClick={() => editarPerfil(row.original)}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Excluir perfil fiscal">
                                <span>
                                    <IconButton
                                        color="error"
                                        disabled={!podeCancelar()}
                                        onClick={() => setPerfilExcluir(row.original)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>

            <Dialog open={Boolean(perfilExcluir)} onClose={() => setPerfilExcluir(null)}>
                <DialogTitle>Excluir perfil fiscal?</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        O perfil{' '}
                        <strong>
                            {perfilExcluir?.codigo} — {perfilExcluir?.descricao}
                        </strong>{' '}
                        será excluído. A operação será bloqueada se existirem produtos vinculados.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setPerfilExcluir(null)}>Cancelar</Button>

                    <Button variant="contained" color="error" onClick={confirmarExclusao}>
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Index;
